import { Component, ElementRef, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { UtilsService } from '@services/common/utils.service';
import { OrganizationAction } from '@state/organization.state';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { ConfirmationService } from 'primeng/api';
import { combineLatest, Subject } from 'rxjs';
import { PostHogService } from '@services/common/post-hog.service';
import { destructiveConfirmation } from 'app/main/helpers/confirmation';
import { revealFormErrors } from 'app/main/helpers/form-validation';

@Component({
    selector: 'app-edit-period',
    templateUrl: './edit-period.component.html',
    styleUrls: ['./edit-period.component.scss'],
    standalone: false,
})
export class EditPeriodComponent implements OnInit {
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    private _period: number;

    // The @Input() decorator marks this setter as an input property,
    // meaning it can receive values from a parent component.
    @Input()
    set period(value: number | undefined) {
        if (value) {
            this._period = value;
        }
    }

    // Getter to access the normalized "period" value
    get period(): number {
        return this._period;
    }
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    @Input() public availableYears: number[];
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    @Input() public organizationId: string;
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    @Input() public activeQuestionnaire: QuestionnaireDto;
    @Input() public questionnaires?: QuestionnaireDto[];
    @Output() closeEditPeriod = new EventEmitter<{ translateKey: string; status: boolean } | undefined>();
    public yearInDropdown?: number[];
    public periodForm: FormGroup;

    constructor(
        private fb: FormBuilder,
        private store: Store,
        private postHogService: PostHogService,
        private confirmationService: ConfirmationService,
        private translateService: TranslateService,
        private utilsService: UtilsService,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        this.periodForm = this.fb.group({
            year: new FormControl('', Validators.required),
        });
    }

    ngOnInit(): void {
        this.yearInDropdown = [...this.availableYears];
        if (this.period) {
            this.yearInDropdown.push(this.period);
            this.yearInDropdown.sort((a, b) => a - b);
            this.periodForm.get('year')?.setValue(this.period);
        }
    }

    save(): void {
        if (!revealFormErrors(this.periodForm, this.host.nativeElement)) {
            return;
        }
        if (!this.period) {
            this.createQuestionnaires(this.periodForm.get('year')?.value as number);
        } else {
            this.updateQuestionnairesPeriod();
        }
    }

    createQuestionnaires(year: number) {
        if (this.periodForm.valid) {
            const callCompletion: Subject<QuestionnaireDto[] | undefined> = new Subject();
            this.store.dispatch(
                new OrganizationAction.AddQuestionnairesFromPeriodAction(this.organizationId, year, callCompletion),
            );
            callCompletion.subscribe({
                next: () => {
                    this.postHogService.capture('period_created', { year });
                    this.closeEditPeriod.emit({
                        translateKey: 'components.settingsForm.periodSection.toasts.creationSuccess',
                        status: true,
                    });
                },
                error: () => {
                    this.closeEditPeriod.emit({
                        translateKey: 'components.settingsForm.periodSection.toasts.creationFail',
                        status: false,
                    });
                },
            });
        }
    }

    public updateQuestionnairesPeriod() {
        if (this.period) {
            const callCompletion = new Subject<QuestionnaireDto[] | undefined>();
            this.store.dispatch(
                new OrganizationAction.EditQuestionnairesPeriodAction(
                    this.organizationId,
                    this.period,
                    this.periodForm.get('year')?.value as number,
                    this.activeQuestionnaire,
                    callCompletion,
                ),
            );
            callCompletion.subscribe({
                next: () => {
                    this.closeEditPeriod.emit({
                        translateKey: 'components.settingsForm.periodSection.toasts.updateSuccess',
                        status: true,
                    });
                },
                error: () => {
                    this.closeEditPeriod.emit({
                        translateKey: 'components.settingsForm.periodSection.toasts.updateFail',
                        status: false,
                    });
                },
            });
        }
    }

    onDelete(): void {
        combineLatest([
            this.translateService.get('common.confirm_message'),
            this.translateService.get('common.confirm_title'),
            this.translateService.get('common.confirm_button'),
            this.translateService.get('common.cancel_button'),
        ]).subscribe((translations) => {
            this.confirmationService.confirm(
                destructiveConfirmation({
                    message: this.utilsService.handleTranslation(translations[0] as string),
                    header: this.utilsService.handleTranslation(translations[1] as string),
                    acceptLabel: this.utilsService.handleTranslation(translations[2] as string),
                    rejectLabel: this.utilsService.handleTranslation(translations[3] as string),
                    icon: 'ms ms-warning',
                    accept: () => {
                        if (this.period) {
                            const callCompletion = new Subject<QuestionnaireDto[] | undefined>();
                            this.store.dispatch(
                                new OrganizationAction.DeleteQuestionnairesFromPeriodAction(
                                    this.organizationId,
                                    this.period,
                                    this.activeQuestionnaire,
                                    callCompletion,
                                ),
                            );
                            callCompletion.subscribe({
                                next: () => {
                                    this.closeEditPeriod.emit({
                                        translateKey: 'components.settingsForm.periodSection.toasts.deteteSuccess',
                                        status: true,
                                    });
                                },
                                error: () => {
                                    this.closeEditPeriod.emit({
                                        translateKey: 'components.settingsForm.periodSection.toasts.deleteFail',
                                        status: false,
                                    });
                                },
                            });
                        }
                    },
                }),
            );
        });
    }

    close(): void {
        this.closeEditPeriod.emit();
    }
}
